import React, { useEffect, useState } from 'react';
import { SeatmapSeat, SeatStatus } from '@arema/components/Classes';
import { Button, Header, Message } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import { Modal } from 'semantic-ui-react';
import { Seatmap } from '@arema/components';
import { addCommas, formatSeatNumber } from '@arema/components/Util';
import API from '../ReportsAPI';

interface SeatmapData{
	section_id: number,
	date_id: number,
	section_name: string,
	seats_width: number,
	seats_height: number,
	stage_x: number,
	stage_y: number,
	stage_width: number,
	stage_height: number,
	seatmap: {
		seat_id: number,
		seat_x: number,
		seat_y: number,
		seat_section: string,
		seat_row: string,
		seat_number: string,
		seat_status: SeatStatus,
		status_comment: string,
		seat_cost: number,
		ticket_id: number,
	}[]
}

interface SectionSeatmapModalProps{
	dateId: number,
	sectionId: number,
	open: boolean,
	sold?: number,
	onClose: ()=>void,
}

var SectionSeatmapModal = (props: SectionSeatmapModalProps)=>{
	var { t } = useTranslation();
	var [data, setData] = useState<SeatmapData>(null);
	var [loadError, setLoadError] = useState<string>(null);

	useEffect(()=>{
		if(!props.dateId || !props.sectionId) return;
		if(data && data.date_id===props.dateId && data.section_id===props.sectionId) return;
		setLoadError(null);
		setData(null);
		API.getSeatmap(props.dateId, props.sectionId).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'ST-LCL-SCSMMD-1' }));
		})
	}, [props.dateId, props.sectionId]);

	if(!props.dateId || !props.sectionId) return null;

	var map : SeatmapSeat[][] = [];
	var sold_tickets = 0;
	if(data){
		var has_stage = (data.stage_width && data.stage_width>0) && (data.stage_height && data.stage_height>0) && (data.stage_x && data.stage_y);
		for(let y=0; y<data.seats_height; y++){
			var row : SeatmapSeat[] = [];
			for(let x=0; x<data.seats_width; x++){
				var is_stage = has_stage && (data.stage_x<=x && (data.stage_x+data.stage_width)>x) && (data.stage_y<=y && (data.stage_y+data.stage_height)>y);
				if(is_stage){
					row.push({
						seat_id: null,
						seat_x: x,
						seat_y: y,
						stage: true,
					});
				}else{
					var seat = data.seatmap.find(a=>a.seat_x===x && a.seat_y===y);
					row.push(seat || null);
					if(seat && seat.ticket_id) sold_tickets += 1;
				}
			}
			map.push(row);
		}
	}
	
	return <Modal open={props.open} onClose={props.onClose} size='fullscreen'>
		<Modal.Header>{t('common.seats')}{data ? ':' : ''} {data?.section_name}</Modal.Header>
		<Modal.Content style={{ minHeight: 320, padding: 0 }}>
			{loadError ? (
				<Header text={t('common.error')} subtext={loadError} style={{ marginTop: 100, marginBottom: 100, paddingTop: 0 }} />
			) : data ? (
				<Seatmap style={{ overflow: 'scroll', height: 400 }} containerStyle={{ justifyContent: 'unset', padding: 15 }}>
					{!!props.sold && props.sold>sold_tickets && (
						<Message style={{ marginBottom: 15, marginLeft: 10, maxWidth: 500 }}>
							{t('seatmap.sold_deleted', { count: props.sold-sold_tickets })}
						</Message>
					)}
					{map.map((r, i)=>(
						<Seatmap.Row key={`SMPMDLR-${i}`}>
							{r.map((a, si)=>{
								var key = `SMPMDLS-${a?.seat_id}-${si}`;
								if(!a){
									return <Seatmap.Seat key={key} empty />
								}else if(a && a.stage){
									return <Seatmap.Seat key={key} stage={[1,1,1,1]} style={{ marginTop: 2, marginBottom: 2 }} />
								}
								return <Seatmap.Seat
									key={key}
									status={a.seat_status}
									color={a.ticket_id ? 'green' : null}
									iconName={a.seat_status===SeatStatus.LOCKED ? 'lock' : null}
									text={formatSeatNumber(a.seat_row, a.seat_number)}
									subtext={a.seat_cost ? `$${addCommas(a.seat_cost, true, true)}` : a.seat_section}
								/>
							})}
						</Seatmap.Row>
					))}
				</Seatmap>
			) : (
				<Header loading text={t('seatmap.loading')} style={{ marginTop: 100, marginBottom: 100, paddingTop: 0 }} />
			)}
		</Modal.Content>
		<Modal.Actions>
			<Button text={t('common.close')} onClick={props.onClose} />
		</Modal.Actions>
	</Modal>
}

export default SectionSeatmapModal;